<script lang="ts">
  import { onMount } from 'svelte'
  import { defaultSignalUrl, SignalingClient } from './signaling'
  import { parseSharedRoomTarget } from './room-target'
  import { isMobileBuild } from './platform'
  import { ViewerConnection } from './viewer-rtc'

  let { room, active }: { room: { code: string; share_url?: string | null }; active: boolean } =
    $props()
  let video: HTMLVideoElement
  let still = $state('')
  let visible = $state(false)
  let hidden = $state(false)
  let ready = $state(false)

  onMount(() => {
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
    })
    observer.observe(video)
    const visibility = () => {
      hidden = document.hidden
    }
    visibility()
    document.addEventListener('visibilitychange', visibility)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', visibility)
    }
  })

  $effect(() => {
    const key = room.share_url || room.code
    void key
    still = ''
  })

  $effect(() => {
    if (!visible || hidden || (!active && still)) return
    const hovering = active
    let target
    try {
      target = parseSharedRoomTarget(room, defaultSignalUrl(), !isMobileBuild)
    } catch {
      return
    }
    let rtc: ViewerConnection | null = null
    let host = ''
    let disposed = false
    ready = false
    const client = new SignalingClient(target.signalUrl, {
      autoReconnect: false,
      handlers: {
        onOpen: () => client.send(client.joinMessage(target.code, 'Preview')),
        onMessage: (message) => {
          if (disposed) return
          if (message.type === 'room_joined') {
            host = message.host.id
            rtc = new ViewerConnection(
              message.ice_servers,
              (payload) => client.send({ type: 'signal', to: host, payload }),
              {
                onStream: (stream) => {
                  video.srcObject = stream
                  void video.play().catch(() => {})
                },
                onState: (state) => {
                  if (state === 'failed') stop()
                },
              },
            )
          } else if (message.type === 'signal' && message.from === host) {
            void rtc?.handle(message.payload).catch(() => stop())
          } else if (message.type === 'error' || message.type === 'room_closed') stop()
        },
        onError: () => stop(),
      },
    })
    const stop = () => {
      disposed = true
      rtc?.close()
      client.close()
      video.srcObject = null
      ready = false
    }
    const snapshot = () => {
      if (!video.videoWidth || video.readyState < 2) return
      const canvas = document.createElement('canvas')
      canvas.width = 480
      canvas.height = Math.round((480 * video.videoHeight) / video.videoWidth)
      const context = canvas.getContext('2d')
      if (!context) return
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      still = canvas.toDataURL('image/jpeg', 0.75)
    }
    const loaded = () => {
      ready = true
      if (!hovering) snapshot()
    }
    video.addEventListener('loadeddata', loaded)
    const timeout = setTimeout(() => {
      if (!ready) stop()
    }, 12000)
    client.connect()
    return () => {
      if (hovering) snapshot()
      clearTimeout(timeout)
      video.removeEventListener('loadeddata', loaded)
      stop()
    }
  })
</script>

{#if still}<img src={still} alt="" />{/if}
<video bind:this={video} muted autoplay playsinline class:playing={active && ready}></video>

<style>
  img,
  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  video {
    opacity: 0;
  }
  video.playing {
    opacity: 1;
  }
</style>
